import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { STAGES, STAGE_LABEL, fmtMoney, fmtDate } from "@/lib/constants";
import { ScoreBadge } from "@/components/RiskDot";
import DocViewer from "@/components/DocViewer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  ArrowLeft, Sparkle, ArrowsClockwise, Rocket, Plus, CheckCircle, Circle, Trash, CalendarCheck,
  File as FileIcon, DownloadSimple, Eye,
} from "@phosphor-icons/react";

function Field({ label, value }) {
  return (
    <div>
      <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-1 text-sm">{value || "—"}</div>
    </div>
  );
}

export default function LeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const [lead, setLead] = useState(null);
  const [acts, setActs] = useState([]);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [newAct, setNewAct] = useState({ type: "nota", title: "", description: "" });
  const [busy, setBusy] = useState(false);
  const [est, setEst] = useState(null);
  const [estBusy, setEstBusy] = useState(false);
  const [docs, setDocs] = useState([]);
  const [viewFile, setViewFile] = useState(null);

  const canEdit = hasRole("vanzari", "pm");

  const load = () => {
    api.get(`/leads/${id}`).then((r) => setLead(r.data)).catch(() => navigate("/leads"));
    api.get(`/activities?lead_id=${id}`).then((r) => setActs(r.data)).catch(() => {});
    api.get(`/leads/${id}/documents`).then((r) => setDocs(r.data || [])).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  useEffect(() => {
    if (!lead) return;
    setEstBusy(true);
    api.post("/visio/estimate", { project_type: lead.product_type || undefined })
      .then((r) => setEst(r.data)).catch(() => setEst(null))
      .finally(() => setEstBusy(false));
    /* eslint-disable-next-line */
  }, [lead?.id, lead?.product_type]);

  if (!lead) return <div className="p-8 text-muted-foreground">Se încarcă…</div>;

  const rescore = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/leads/${id}/rescore`);
      setLead(data);
      toast.success("Scor AI recalculat");
    } catch { toast.error("Eroare la scorare"); }
    finally { setBusy(false); }
  };

  const convert = async () => {
    const code = window.prompt("Cod proiect (obligatoriu, ex: HGS 1904):", "");
    if (code === null) return;
    if (!code.trim()) return toast.error("Codul proiectului este obligatoriu");
    setBusy(true);
    try {
      const { data } = await api.post(`/leads/${id}/convert`, { code: code.trim() });
      toast.success("Lead convertit în proiect");
      navigate(`/projects/${data.id}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const openEdit = () => {
    setForm({ ...lead, contact: { ...lead.contact } });
    setEditing(true);
  };
  const saveEdit = async () => {
    try {
      const { data } = await api.put(`/leads/${id}`, {
        company: form.company, country: form.country, product_type: form.product_type,
        estimated_value: Number(form.estimated_value) || 0, source: form.source,
        stage: form.stage, notes: form.notes, contact: form.contact,
      });
      setLead(data);
      setEditing(false);
      toast.success("Lead actualizat");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const addAct = async () => {
    if (!newAct.title.trim()) return;
    const { data } = await api.post("/activities", { ...newAct, lead_id: id });
    setActs((a) => [data, ...a]);
    setNewAct({ type: "nota", title: "", description: "" });
  };
  const toggleAct = async (a) => {
    const { data } = await api.put(`/activities/${a.id}`, { done: !a.done });
    setActs((list) => list.map((x) => (x.id === a.id ? data : x)));
  };
  const delAct = async (a) => {
    await api.delete(`/activities/${a.id}`);
    setActs((list) => list.filter((x) => x.id !== a.id));
  };

  return (
    <div className="mx-auto max-w-6xl px-6 py-8 fade-up" data-testid="lead-detail">
      <Link to="/leads" className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft size={16} /> Înapoi la lead-uri
      </Link>

      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-head text-3xl font-extrabold tracking-tighter">{lead.company}</h1>
            <ScoreBadge score={lead.ai_score} color={lead.score_color} />
          </div>
          <p className="mt-1 text-sm text-muted-foreground">{lead.product_type || "—"} · {STAGE_LABEL[lead.stage]}</p>
        </div>
        {canEdit && (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={rescore} disabled={busy} data-testid="rescore-btn">
              <ArrowsClockwise size={16} className="mr-1.5" /> Rescor AI
            </Button>
            <Button variant="outline" onClick={openEdit} data-testid="edit-lead-btn">Editează</Button>
            {!lead.converted_project_id ? (
              <Button onClick={convert} disabled={busy} data-testid="convert-btn">
                <Rocket size={16} weight="bold" className="mr-1.5" /> Convertește în proiect
              </Button>
            ) : (
              <Button variant="outline" onClick={() => navigate(`/projects/${lead.converted_project_id}`)}>
                Vezi proiectul
              </Button>
            )}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <div className="rounded-xl border border-border bg-card p-6">
            <h3 className="mb-4 font-head text-lg font-bold">Detalii lead</h3>
            <div className="grid grid-cols-2 gap-5 sm:grid-cols-3">
              <Field label="Țară" value={lead.country} />
              <Field label="Sursă" value={lead.source} />
              <Field label="Valoare" value={fmtMoney(lead.estimated_value)} />
              <Field label="Contact" value={lead.contact?.name} />
              <Field label="Funcție" value={lead.contact?.role} />
              <Field label="Email" value={lead.contact?.email} />
              <Field label="Telefon" value={lead.contact?.phone} />
              <Field label="Creat" value={fmtDate(lead.created_at)} />
            </div>
            {lead.notes && (
              <div className="mt-5 border-t border-border pt-4">
                <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Note</div>
                <p className="mt-1 text-sm text-muted-foreground">{lead.notes}</p>
              </div>
            )}
          </div>

          <div className="rounded-xl border border-border bg-card p-6">
            <h3 className="mb-3 font-head text-lg font-bold">Activități</h3>
            {canEdit && (
              <div className="mb-4 flex flex-wrap gap-2">
                <Select value={newAct.type} onValueChange={(v) => setNewAct((a) => ({ ...a, type: v }))}>
                  <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["apel", "email", "intalnire", "nota", "task"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input placeholder="Titlu activitate…" value={newAct.title}
                  onChange={(e) => setNewAct((a) => ({ ...a, title: e.target.value }))}
                  className="flex-1 min-w-[160px]" data-testid="activity-title" />
                <Button onClick={addAct} data-testid="add-activity-btn"><Plus size={16} /></Button>
              </div>
            )}
            <div className="space-y-2">
              {acts.map((a) => (
                <div key={a.id} className="flex items-start gap-3 rounded-lg border border-border px-3 py-2.5" data-testid={`activity-${a.id}`}>
                  <button onClick={() => toggleAct(a)} className="mt-0.5 text-muted-foreground hover:text-emerald-400">
                    {a.done ? <CheckCircle size={18} weight="fill" className="text-emerald-400" /> : <Circle size={18} />}
                  </button>
                  <div className="min-w-0 flex-1">
                    <div className={"text-sm font-medium " + (a.done ? "text-muted-foreground line-through" : "")}>
                      <span className="mr-2 rounded bg-white/5 px-1.5 py-0.5 text-[10px] uppercase text-violet-300">{a.type}</span>
                      {a.title}
                    </div>
                    {a.description && <div className="text-xs text-muted-foreground">{a.description}</div>}
                  </div>
                  <button onClick={() => delAct(a)} className="text-muted-foreground hover:text-red-400"><Trash size={15} /></button>
                </div>
              ))}
              {acts.length === 0 && <p className="text-sm text-muted-foreground">Nicio activitate.</p>}
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="rounded-xl border border-border bg-card p-6">
            <div className="flex items-center gap-2">
              <Sparkle size={18} weight="fill" className="text-violet-400" />
              <h3 className="font-head text-lg font-bold">Scor AI</h3>
            </div>
            <div className="mt-4 flex items-center gap-4">
              <div className="font-head text-5xl font-extrabold tabular-nums">{lead.ai_score}</div>
              <ScoreBadge score={lead.ai_score} color={lead.score_color} />
            </div>
            <p className="mt-3 text-sm text-muted-foreground">{lead.ai_reason || "Fără explicație."}</p>
          </div>

          <div className="rounded-xl border border-border bg-card p-6" data-testid="lead-history-card">
            <h3 className="mb-4 font-head text-lg font-bold">Istoric lead</h3>
            <ol className="relative space-y-4 border-l border-border pl-5">
              {(lead.stage_history || []).length === 0 && (
                <li className="text-sm text-muted-foreground">Nicio schimbare de etapă înregistrată încă. Etapa curentă: <span className="font-semibold text-foreground">{STAGE_LABEL[lead.stage] || lead.stage}</span>.</li>
              )}
              {(lead.stage_history || []).map((h, i) => (
                <li key={i} className="relative" data-testid={`lead-history-${i}`}>
                  <span className="absolute -left-[23px] top-1 h-2.5 w-2.5 rounded-full bg-primary" />
                  <div className="text-sm font-semibold">{STAGE_LABEL[h.stage] || h.stage}</div>
                  <div className="text-[11px] text-muted-foreground">
                    {h.from ? `din „${STAGE_LABEL[h.from] || h.from}" · ` : ""}{fmtDate(h.at)}{h.by ? ` · ${h.by}` : ""}
                  </div>
                </li>
              ))}
            </ol>
          </div>

          <div className="rounded-xl border border-border bg-card p-6" data-testid="ai-estimate-card">
            <div className="flex items-center gap-2">
              <CalendarCheck size={18} weight="fill" className="text-emerald-400" />
              <h3 className="font-head text-lg font-bold">Estimare termen (Visio AI)</h3>
            </div>
            {estBusy ? (
              <p className="mt-4 text-sm text-muted-foreground">Se calculează termenul realist…</p>
            ) : est ? (
              <>
                <div className="mt-4">
                  <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Termen realist recomandat</div>
                  <div className="mt-1 font-head text-2xl font-extrabold tabular-nums text-emerald-400" data-testid="ai-estimate-date">{est.estimated_date}</div>
                  <div className="mt-0.5 text-xs text-muted-foreground">~{est.estimated_business_days} zile lucrătoare (buffer {est.buffer_pct}%)</div>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-3 border-t border-border pt-3 text-xs">
                  <div><span className="text-muted-foreground">Workflow</span><div className="font-semibold tabular-nums">{est.workflow_days} zile</div></div>
                  <div><span className="text-muted-foreground">Istoric ({est.samples} mostre)</span><div className="font-semibold tabular-nums">{est.historical_days || "—"} zile</div></div>
                </div>
                <p className="mt-3 rounded-md bg-white/[0.03] px-2.5 py-2 text-[11px] text-muted-foreground">
                  Folosește această dată la ofertare. Metodă: <span className="font-semibold text-foreground">{est.method}</span>{lead.product_type ? ` · tip: ${lead.product_type}` : " · toate tipurile"}.
                </p>
              </>
            ) : (
              <p className="mt-4 text-sm text-muted-foreground">Estimare indisponibilă momentan.</p>
            )}
          </div>
          <div className="rounded-xl border border-border bg-card p-6" data-testid="lead-documents-card">
            <h3 className="mb-3 font-head text-lg font-bold">Documente lead ({docs.length})</h3>
            <div className="space-y-2">
              {docs.map((f) => (
                <div key={f.id} className="flex items-center gap-2 rounded-lg border border-border px-3 py-2" data-testid={`lead-doc-${f.id}`}>
                  <FileIcon size={17} className="shrink-0 text-blue-400" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm">{f.name}</div>
                    <div className="text-[11px] text-muted-foreground">{fmtDate(f.created_at)} · {f.uploaded_by_name || f.uploaded_by_email || "—"}</div>
                  </div>
                  <button onClick={() => setViewFile(f)} className="text-muted-foreground hover:text-primary" title="Vizualizează" data-testid={`lead-doc-view-${f.id}`}><Eye size={17} /></button>
                  <a href={fileUrl(f.storage_path)} download={f.name} className="text-muted-foreground hover:text-foreground" data-testid={`lead-doc-dl-${f.id}`}><DownloadSimple size={17} /></a>
                </div>
              ))}
              {docs.length === 0 && <p className="text-sm text-muted-foreground">Niciun document încărcat pe acest lead. Se adaugă din hub-ul de departament → comutator „Lead".</p>}
            </div>
            {lead.converted_project_id && <p className="mt-3 text-[11px] text-emerald-400/80">La conversie, aceste documente au fost copiate și în dosarul digital al proiectului.</p>}
          </div>
        </div>
      </div>

      <Dialog open={editing} onOpenChange={setEditing}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Editează lead</DialogTitle></DialogHeader>
          {form && (
            <div className="grid max-h-[65vh] gap-3 overflow-y-auto pr-1">
              <div className="grid gap-1.5"><Label>Companie</Label><Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="grid gap-1.5"><Label>Țară</Label><Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} /></div>
                <div className="grid gap-1.5"><Label>Produs</Label><Input value={form.product_type} onChange={(e) => setForm({ ...form, product_type: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="grid gap-1.5"><Label>Valoare (€)</Label><Input type="number" value={form.estimated_value} onChange={(e) => setForm({ ...form, estimated_value: e.target.value })} /></div>
                <div className="grid gap-1.5">
                  <Label>Etapă</Label>
                  <Select value={form.stage} onValueChange={(v) => setForm({ ...form, stage: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{STAGES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid gap-1.5"><Label>Sursă</Label><Input value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <Input placeholder="Contact nume" value={form.contact.name} onChange={(e) => setForm({ ...form, contact: { ...form.contact, name: e.target.value } })} />
                <Input placeholder="Funcție" value={form.contact.role} onChange={(e) => setForm({ ...form, contact: { ...form.contact, role: e.target.value } })} />
                <Input placeholder="Email" value={form.contact.email} onChange={(e) => setForm({ ...form, contact: { ...form.contact, email: e.target.value } })} />
                <Input placeholder="Telefon" value={form.contact.phone} onChange={(e) => setForm({ ...form, contact: { ...form.contact, phone: e.target.value } })} />
              </div>
              <div className="grid gap-1.5"><Label>Note</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            </div>
          )}
          <DialogFooter><Button onClick={saveEdit} data-testid="save-lead-edit">Salvează</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <DocViewer file={viewFile} onClose={() => setViewFile(null)} />
    </div>
  );
}
